import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const createUserDetailsQueryString = ({ page, limit, email, name, phone, userId, city, country, status } = {}) => {
  const params = new URLSearchParams();

  if (page !== undefined && page !== null) params.set('page', page);
  if (limit !== undefined && limit !== null) params.set('limit', limit);
  if (email) params.set('user_email', email);
  if (name) params.set('first_name', name);
  if (phone) params.set('user_phone', phone);
  if (userId) params.set('user_id', userId);
  if (city) params.set('city', city);
  if (country) params.set('country', country);
  if (status) params.set('status', status);

  return params.toString();
};

const createUserConnectionQueryString = ({ userId, connectionType, page, limit } = {}) => {
  const params = new URLSearchParams();

  if (userId) params.set('user_id', userId);
  if (connectionType) params.set('connectionType', connectionType);
  if (page !== undefined && page !== null) params.set('page', page);
  if (limit !== undefined && limit !== null) params.set('limit', limit);

  return params.toString();
};

const convertDateToYMD = dateInput => {
  const date = new Date(dateInput);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const day = `0${date.getDate()}`.slice(-2);

  return `${date.getFullYear()}-${month}-${day}`;
};

const formatDayByQuery = (date, dayOffset) => {
  const dateValue = new Date(String(date).replace(/-/g, '/'));
  dateValue.setDate(dateValue.getDate() + Number(dayOffset));

  return convertDateToYMD(dateValue);
};

const buildVodAnalyticsReportFilterUrl = ({ pubId, data = {}, userId }) => {
  let resolvedPubId = pubId;
  if (data?.publishers?.pubid) {
    resolvedPubId = data.publishers.pubid;
  }

  let url = `/report/videoAnalytics?publisherchecked=1&publisher_id=${resolvedPubId}&role_id=${localStorage.getItem('role_id')}&`;
  url += `startDate=${formatDayByQuery(data?.start_date, 0)}`;
  url += `&endDate=${formatDayByQuery(data?.end_date, 0)}`;

  if (localStorage.getItem('contentPartnerId')) {
    url += `&content_partner_id=${localStorage.getItem('contentPartnerId')}`;
  }

  if (data?.isContentPartnerSelected) {
    url += '&content_partner_checked=1';
    if (data?.contentPartner?.content_partner_id) {
      url += `&content_partner_id=${data.contentPartner.content_partner_id}`;
    }
  }

  if (data?.isCountrySelected) {
    url += '&countrychecked=1';
    if (data?.country?.value && data.country.value !== 1) {
      url += `&country=${data.country.label}`;
    }
  }

  if (data?.isAppSelected) {
    url += '&appchecked=1';
    if (data?.appType?.id) {
      url += `&app_id=${data.appType.id}`;
    }
    if (data?.appType?.label) {
      url += `&app_name=${data.appType.label}`;
    }
  }

  if (data?.isChannelSelected) {
    url += '&channelchecked=1';
    if (data?.channel?.channel_id) {
      url += `&channel_id=${data.channel.channel_id}`;
    }
  }

  if (data?.isShowchecked) {
    url += '&showchecked=1';
    if (data?.show?.id) {
      url += `&show_id=${data.show.id}`;
    }
  }

  if (data?.isEpisodeChecked) {
    url += '&episodechecked=1';
    if (data?.episode?.id) {
      url += `&video_id=${data.episode.id}`;
    }
  }

  if (data?.isCollectionchecked) {
    url += '&collectionchecked=1';
    if (data?.collection?.id) {
      url += `&show_id=${data.collection.id}`;
    }
  }

  if (data?.isCollectionVideoChecked) {
    url += '&collectionvideochecked=1';
    if (data?.collectionVideo?.id) {
      url += `&video_id=${data.collectionVideo.id}`;
    }
  }

  if (data?.isVideoChecked) {
    url += '&videochecked=1';
    if (data?.video?.id) {
      url += `&video_id=${data.video.id}`;
    }
  }

  if (data?.subType?.subscription_type_id) {
    url += `&subscription_type=${data.subType.subscription_type_id}`;
  }

  if (data?.paymentMode?.value && data.paymentMode.value !== 1) {
    url += `&mode_of_payment='${data.paymentMode.label}'`;
  }

  if (data?.isCitySelected) {
    url += '&citychecked=1';
    if (data?.city?.label && data.city.label !== 'All') {
      url += `&city=${data.city.label}`;
    }
  }

  if (data?.isMonthlySelected) {
    url += '&monthly_checked=1';
  }

  if (data?.isMediaIdSelected) {
    url += '&media_id_checked=1';
  }

  if (data?.isUserSelected) {
    url += '&subscription_type_checked=1';
    if (data?.selected_user_type?.value) {
      url += `&subscription_type=${data.selected_user_type.value}`;
    }
  }

  if (data?.ai_type) {
    url += '&ai_type=1';
    if (data?.aiType?.label && data.aiType.label !== 'All') {
      url += `&ai_type_value=${data.aiType.label}`;
    }
  }

  if (data?.isAggregateSelected) {
    url += '&aggregate_checked=1';
  }

  if (userId) {
    url += `&user_id=${userId}`;
  }

  return { url, resolvedPubId };
};

const buildVodAnalyticsGraphUrl = ({ pubId, channels, filter = {}, userId }) => {
  let endDate = filter?.end_date
    ? new Date(filter.end_date).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  let startDate = filter?.start_date
    ? new Date(filter.start_date).toISOString().slice(0, 10)
    : new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  if (startDate > endDate) {
    const tempDate = startDate;
    startDate = endDate;
    endDate = tempDate;
  }

  let url = `/report/videoAnalytics?startDate=${startDate}&endDate=${endDate}&publisherchecked=1&publisher_id=${pubId}&role_id=${localStorage.getItem('role_id')}`;

  if (channels && channels[0]) {
    const selectedChannel = channels[0];
    url += '&channelchecked=1';
    if (selectedChannel?.channel_id) {
      url += `&channel_id=${selectedChannel.channel_id}`;
    }
  }

  if (userId) {
    url += `&user_id=${userId}`;
  }

  if (localStorage.getItem('contentPartnerId')) {
    url += `&content_partner_id=${localStorage.getItem('contentPartnerId')}`;
  }

  return url;
};

const buildVodAnalyticsDefaultUrl = ({ pubId, channels, userId }) => {
  const today = new Date().toISOString().slice(0, 10);
  const endDate = formatDayByQuery(today, 0);
  const startDate = formatDayByQuery(today, -6);
  let url = `/report/videoAnalytics?startDate=${startDate}&endDate=${endDate}&publisherchecked=1&publisher_id=${pubId}&role_id=${localStorage.getItem('role_id')}`;

  if (channels && channels[0]) {
    const selectedChannel = channels[0];
    url += '&channelchecked=1';
    if (selectedChannel?.channel_id) {
      url += `&channel_id=${selectedChannel.channel_id}`;
    }
  }

  if (userId) {
    url += `&user_id=${userId}`;
  }

  if (localStorage.getItem('contentPartnerId')) {
    url += `&content_partner_id=${localStorage.getItem('contentPartnerId')}`;
  }

  return url;
};

export const userManagementApi = createApi({
  reducerPath: 'userManagementApi',
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_SERVER_HOST,
    prepareHeaders: headers => {
      const accessToken = localStorage.getItem('access-token');
      const pubId = localStorage.getItem('pub_id');
      const roleId = localStorage.getItem('role_id');

      if (accessToken) headers.set('access-token', accessToken);
      if (pubId) headers.set('pubid', pubId);
      if (roleId) headers.set('role_id', roleId);

      return headers;
    }
  }),
  tagTypes: ['UserDetails', 'Subscriptions', 'Transactions', 'UserOverview', 'UserNotes', 'UserDevices', 'UserConnections', 'CommunitySettings', 'UserStreak', 'ExportJobs'],
  endpoints: builder => ({
    getUserDetails: builder.query({
      query: queryParams => `/user/details?${createUserDetailsQueryString(queryParams)}`,
      providesTags: ['UserDetails']
    }),
    exportUsers: builder.mutation({
      query: payload => ({
        url: '/user/export',
        method: 'POST',
        body: payload || {}
      }),
      invalidatesTags: ['ExportJobs']
    }),
    getExportJobs: builder.query({
      query: ({ page = 1, pageSize = 10, search = '' } = {}) => {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(pageSize)
        });
        if (search) params.set('search', search);
        return `/user/export/jobs?${params.toString()}`;
      },
      providesTags: ['ExportJobs']
    }),
    getExportJob: builder.query({
      query: jobId => `/user/export/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'ExportJobs', id: jobId }]
    }),
    getUserSubscriptions: builder.query({
      query: userId => `/user/subscription/list${userId ? `?user_id=${userId}` : ''}`,
      providesTags: ['Subscriptions']
    }),
    getUserTransactions: builder.query({
      query: userId => `/user/transaction/history${userId ? `?user_id=${userId}` : ''}`,
      providesTags: ['Transactions']
    }),
    addUser: builder.mutation({
      query: payload => ({
        url: '/user/add',
        method: 'POST',
        body: {
          user_email: payload.email,
          country: payload.country,
          first_name: payload.firstName,
          last_name: payload.lastName,
          password: payload.password,
          phone: payload.phone
        }
      }),
      invalidatesTags: ['UserDetails']
    }),
    updateUserDetails: builder.mutation({
      query: payload => ({
        url: '/user/update',
        method: 'POST',
        body: {
          user_email: payload.email,
          user_id: payload.user_id,
          country: payload.country,
          first_name: payload.first_name,
          last_name: payload.last_name,
          password: payload.password,
          phone: payload.phone
        }
      }),
      invalidatesTags: (_result, _error, payload) => [
        'UserDetails',
        payload?.user_id ? { type: 'UserOverview', id: payload.user_id } : 'UserOverview'
      ]
    }),
    deleteUser: builder.mutation({
      query: userId => ({
        url: '/user/delete',
        method: 'POST',
        body: { user_id: userId }
      }),
      invalidatesTags: ['UserDetails', 'Subscriptions']
    }),
    getUserOverview: builder.query({
      query: userId => `/user/overview?user_id=${userId}`,
      providesTags: (_result, _error, userId) => [{ type: 'UserOverview', id: userId }]
    }),
    getSubscriptionType: builder.query({
      query: () => '/subscription/active/',
      providesTags: ['Subscriptions']
    }),
    getCurrency: builder.query({
      query: () => '/currency/list?'
    }),
    getDeviceType: builder.query({
      query: () => '/app/list'
    }),
    getSpecificUserDetails: builder.query({
      query: userSubscriptionId => `/user/subscription/${userSubscriptionId}`,
      providesTags: ['Subscriptions']
    }),
    getChangePlanOptions: builder.query({
      query: planId => `/user/subscription/planchange_options?id=${planId}`
    }),
    addSubscription: builder.mutation({
      query: payload => ({
        url: '/user/subscription/add?',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Subscriptions', 'Transactions','UserDetails','UserOverview']
    }),
    updateSubscriptionAction: builder.mutation({
      query: ({ buttonName, userSubscriptionId, transactionId }) => {
        let url = '/user/subscription/type/update?';

        switch (buttonName) {
          case 'renew':
            url = `${url}user_subscription_id=${userSubscriptionId}&transaction_type=2`;
            break;
          case 'refund':
            url = `${url}user_subscription_id=${userSubscriptionId}&transaction_type=5&transaction_id=${transactionId}`;
            break;
          case 'cancel':
            url = `${url}user_subscription_id=${userSubscriptionId}&transaction_type=3`;
            break;
          case 'void':
            url = `/user/subscription/void?user_subscription_id=${userSubscriptionId}`;
            break;
          default:
            break;
        }

        return {
          url,
          method: 'GET'
        };
      },
      invalidatesTags: ['Subscriptions', 'Transactions','UserDetails','UserOverview']
    }),
    undoSubscriptionCancellation: builder.mutation({
      query: payload => ({
        url: '/user/subscription/cancel/undo',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Subscriptions','UserDetails','UserOverview']
    }),
    changePricingPlan: builder.mutation({
      query: payload => ({
        url: '/user/subscription/change/plan',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Subscriptions', 'Transactions','UserDetails','UserOverview']
    }),
    extendBillingDate: builder.mutation({
      query: payload => ({
        url: '/user/subscription/extend-billing-date',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Subscriptions','UserDetails','UserOverview']
    }),
    getCountryList: builder.query({
      query: (type = '') => `/country/list?type=${type}`
    }),
    getModeOfPayment: builder.query({
      queryFn: async () => ({
        data: [
          { value: 1, label: 'stripe' },
          { value: 2, label: 'paypal' },
          { value: 3, label: 'android-in-app' },
          { value: 4, label: 'ideabiz' },
          { value: 5, label: 'ios-in-app' },
          { value: 6, label: 'roku-in-app' }
        ]
      })
    }),
    addActionNote: builder.mutation({
      query: payload => {
        let publisherUsersId = null;
        if (localStorage.getItem('superadmin') && JSON.parse(localStorage.getItem('superadmin')).role_id == '7') {
          publisherUsersId = JSON.parse(localStorage.getItem('superadmin')).pub_id;
        } else {
          publisherUsersId = localStorage.getItem('user_id') || localStorage.getItem('pub_id');
        }

        return {
          url: '/user/note/add',
          method: 'POST',
          headers: {
            publisher_users_id: publisherUsersId
          },
          body: {
            action_type: payload.action_type,
            note: payload.note,
            user_id: payload.user_id,
            user_subscription_id: payload.user_subscription_id
          }
        };
      },
      invalidatesTags: ['UserNotes']
    }),
    getActivityList: builder.query({
      query: userId => ({
        url: '/user/note/list',
        headers: {
          user_id: userId
        }
      }),
      providesTags: (_result, _error, userId) => [{ type: 'UserNotes', id: userId }]
    }),
    getDeviceInfo: builder.query({
      query: userId => `/user/devices?user_id=${userId}`,
      providesTags: (_result, _error, userId) => [{ type: 'UserDevices', id: userId }]
    }),
    getUserFollowers: builder.query({
      query: ({ userId, page = 1, limit = 100, connectionType = 'followers' }) =>
        `/user/followers?${createUserConnectionQueryString({ userId, connectionType, page, limit })}`,
      providesTags: (_result, _error, { userId }) => [{ type: 'UserConnections', id: `followers-${userId}` }]
    }),
    getUserFollowing: builder.query({
      query: ({ userId, page = 1, limit = 100, connectionType = 'following' }) =>
        `/user/following?${createUserConnectionQueryString({ userId, connectionType, page, limit })}`,
      providesTags: (_result, _error, { userId }) => [{ type: 'UserConnections', id: `following-${userId}` }]
    }),
    getCommunitySettings: builder.query({
      query: params => ({
        url: '/communityV2/settings/list',
        params
      }),
      transformResponse: response => response?.data || [],
      providesTags: (_result, _error, params) => [{ type: 'CommunitySettings', id: params?.user_id || 'LIST' }]
    }),
    updateCommunitySettings: builder.mutation({
      query: payload => ({
        url: '/communityV2/settings/update',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: (_result, _error, payload) => {
        const userIds = Object.values(payload?.data || {}).flatMap(item => [
          ...(item?.new || []),
          ...(item?.delete || [])
        ]);

        const uniqueUserIds = [...new Set(userIds)].filter(Boolean);
        if (uniqueUserIds.length === 0) {
          return [{ type: 'CommunitySettings', id: 'LIST' }];
        }

        return uniqueUserIds.map(userId => ({ type: 'CommunitySettings', id: userId }));
      }
    }),
    getVodAnalyticsReportFilter: builder.query({
      query: ({ pubId, data, userId }) => {
        const { url, resolvedPubId } = buildVodAnalyticsReportFilterUrl({ pubId, data, userId });
        const roleId = localStorage.getItem('role_id');

        if (roleId == '7') {
          return {
            url,
            headers: {
              role_id: roleId || ''
            }
          };
        }

        if (data?.channel?.channel_id) {
          return {
            url,
            headers: {
              pubid: resolvedPubId,
              channelid: data.channel.channel_id
            }
          };
        }

        return {
          url,
          headers: {
            pubid: resolvedPubId
          }
        };
      }
    }),
    getVodAnalyticsReportGraph: builder.query({
      query: ({ pubId, channels, filter, userId }) => ({
        url: buildVodAnalyticsGraphUrl({ pubId, channels, filter, userId }),
        headers: {
          pubid: pubId
        }
      })
    }),
    getVodAnalyticsReportDefault: builder.query({
      query: ({ pubId, channels, userId }) => ({
        url: buildVodAnalyticsDefaultUrl({ pubId, channels, userId }),
        headers: {
          pubid: pubId
        }
      })
    }),
    getShowList: builder.query({
      query: pubId => ({
        url: '/show/list?dropdown=1',
        headers: {
          pubid: pubId,
          channelid: localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      })
    }),
    getVideoList: builder.query({
      query: pubId => ({
        url: '/video/list?type=schedule_dropdown',
        headers: {
          pubid: pubId,
          channelid: localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      })
    }),
    getContentPartnerList: builder.query({
      query: channelId => ({
        url: '/contentPartner/list',
        headers: {
          pubid: localStorage.getItem('pub_id') || '',
          channelid: channelId || ''
        }
      })
    }),
    getCollectionList: builder.query({
      query: pubId => ({
        url: '/collection/list?dropdown=1',
        headers: {
          pubid: pubId,
          channelid: localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      })
    }),
    getEpisodeList: builder.query({
      query: pubId => ({
        url: '/video/list?type=episode',
        headers: {
          pubid: pubId,
          channelid: localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      })
    }),
    getCollectionVideoList: builder.query({
      query: arg => {
        const pubId = typeof arg === 'object' ? arg?.pubId : arg;
        const collectionId = typeof arg === 'object' ? arg?.collectionId : null;

        return ({
        url: collectionId ? `/collection/${collectionId}/videos` : '/video/list?type=collection_video',
        headers: {
          pubid: pubId,
          channelid: localStorage.getItem('currentChannel') || '',
          role_id: localStorage.getItem('role_id') || '',
          content_partner_id: localStorage.getItem('contentPartnerId') || ''
        }
      });
      }
    }),
    getAitypeList: builder.query({
      query: ({ pubId, data }) => {
        const params = new URLSearchParams({
          publisher_id: pubId || ''
        });

        if (data?.start_date) {
          params.append('start_date', data.start_date);
        }

        if (data?.end_date) {
          params.append('end_date', data.end_date);
        }

        return {
          url: `/bigquery/aitype/list?${params.toString()}`,
          headers: {
            publisher_id: pubId || '',
            channelid: localStorage.getItem('currentChannel') || ''
          }
        };
      }
    }),
    sendPasswordResetLink: builder.mutation({
      query: ({ user_id, user_email }) => ({
        url: '/user/passwordReset',
        method: 'POST',
        body: { user_id, user_email }
      })
    }),
    sendUserLoginRequest: builder.mutation({
      query: user_id => {
        const formData = new FormData();
        formData.append('user_id', user_id);

        return {
          url: '/user/request/send',
          method: 'POST',
          body: formData
        };
      }
    }),
    loginUserWithTvCode: builder.mutation({
      query: ({ code, userId }) => {
        const formData = new FormData();
        formData.append('code', code);
        formData.append('user_id', userId);

        return {
          url: '/user/code/enter',
          method: 'POST',
          headers: {
            channelid: localStorage.getItem('currentChannel') || ''
          },
          body: formData
        };
      }
    }),
    resetUserPassword: builder.mutation({
      query: ({ userId, password }) => ({
        url: `/user/${userId}/passwordReset`,
        method: 'POST',
        body: { password }
      })
    }),
    resetDeviceAll: builder.mutation({
      query: userId => ({
        url: `/device/reset/${userId}`,
        method: 'GET'
      }),
      invalidatesTags: (_result, _error, userId) => [{ type: 'UserDevices', id: userId }]
    }),
    recoverUser: builder.mutation({
      query: userId => ({
        url: '/user/recover',
        method: 'POST',
        body: { user_id: userId }
      }),
      invalidatesTags: (_result, _error, userId) => [{ type: 'UserOverview', id: userId }]
    }),
    resetDeviceOne: builder.mutation({
      query: ({ userId, deviceId }) => ({
        url: `/device/reset/${userId}?device_id=${deviceId}`,
        method: 'GET'
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'UserDevices', id: userId }]
    }),
    reSubscribe: builder.mutation({
      query: payload => ({
        url: '/user/subscription/resubscribe',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: (_result, _error, payload) => [
        'Subscriptions',
        payload?.uid ? { type: 'UserOverview', id: payload.uid } : 'UserOverview'
      ]
    }),
    refundTransaction: builder.mutation({
      query: ({ user_subscription_id, transaction_id }) => ({
        url: `/user/subscription/type/update?user_subscription_id=${user_subscription_id}&transaction_type=5&transaction_id=${transaction_id}`,
        method: 'GET'
      }),
      invalidatesTags: ['Transactions', 'Subscriptions']
    }),
    getUserStreakData: builder.query({
      query: ({ userId, startDate, endDate }) =>
        `/user/calendar?start_date=${startDate}&end_date=${endDate}&user_id=${userId}`,
      providesTags: (_result, _error, { userId }) => [{ type: 'UserStreak', id: userId }]
    }),
    updateUserStreak: builder.mutation({
      query: ({ userId, date }) => ({
        url: '/user/streak/update',
        method: 'POST',
        body: { user_id: userId, watch_date: date }
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'UserStreak', id: userId }]
    })
  })
});

export const {
  useGetUserDetailsQuery,
  useExportUsersMutation,
  useGetExportJobsQuery,
  useGetExportJobQuery,
  useGetUserSubscriptionsQuery,
  useGetUserTransactionsQuery,
  useAddUserMutation,
  useUpdateUserDetailsMutation,
  useDeleteUserMutation,
  useGetUserOverviewQuery,
  useGetSubscriptionTypeQuery,
  useGetCurrencyQuery,
  useGetDeviceTypeQuery,
  useGetSpecificUserDetailsQuery,
  useLazyGetSpecificUserDetailsQuery,
  useLazyGetSubscriptionTypeQuery,
  useLazyGetCurrencyQuery,
  useLazyGetDeviceTypeQuery,
  useGetChangePlanOptionsQuery,
  useLazyGetChangePlanOptionsQuery,
  useGetCountryListQuery,
  useGetModeOfPaymentQuery,
  useAddSubscriptionMutation,
  useUpdateSubscriptionActionMutation,
  useUndoSubscriptionCancellationMutation,
  useChangePricingPlanMutation,
  useExtendBillingDateMutation,
  useAddActionNoteMutation,
  useGetActivityListQuery,
  useGetDeviceInfoQuery,
  useGetUserFollowersQuery,
  useGetUserFollowingQuery,
  useGetCommunitySettingsQuery,
  useLazyGetVodAnalyticsReportFilterQuery,
  useLazyGetVodAnalyticsReportGraphQuery,
  useLazyGetVodAnalyticsReportDefaultQuery,
  useLazyGetShowListQuery,
  useLazyGetVideoListQuery,
  useLazyGetContentPartnerListQuery,
  useLazyGetCollectionListQuery,
  useLazyGetEpisodeListQuery,
  useLazyGetCollectionVideoListQuery,
  useLazyGetAitypeListQuery,
  useUpdateCommunitySettingsMutation,
  useSendPasswordResetLinkMutation,
  useSendUserLoginRequestMutation,
  useLoginUserWithTvCodeMutation,
  useResetUserPasswordMutation,
  useResetDeviceAllMutation,
  useRecoverUserMutation,
  useResetDeviceOneMutation,
  useReSubscribeMutation,
  useRefundTransactionMutation,
  useLazyGetUserStreakDataQuery,
  useUpdateUserStreakMutation
} = userManagementApi;
